/**
 * Agent card displayed in the summarization task list.
 */

import type { Id } from "@cvx/_generated/dataModel";
import {
  RotateCcw,
  Loader2,
  ChevronRight,
  FileText,
  Building2,
  Landmark,
  Users,
  BarChart3,
  Banknote,
  Receipt,
  Briefcase,
  Scale,
  ScrollText,
  Package,
  Shield,
  Clock,
  XCircle,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { AGENT_ICONS } from "./agentRegistry";
import { AgentActivityBadge } from "./StatusBadges";
import { WorkflowStatusSelect } from "@/components/tasks/WorkflowStatusSelect";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import {
  formatToolName,
  getToolColor,
  getToolIcon,
  parseActivityMessage,
} from "./shared-panels/tool-display";

// ---------------------------------------------------------------------------
// Category display config
// ---------------------------------------------------------------------------

import type { BadgeColor } from "@/ui/primitives/badge";

const CATEGORY_CONFIG: Record<
  string,
  { label: string; icon: LucideIcon; badgeColor: BadgeColor }
> = {
  corporate_documents: {
    label: "Corporate Documents",
    icon: Building2,
    badgeColor: "blue",
  },
  director_disclosures: {
    label: "Director Disclosures",
    icon: Users,
    badgeColor: "violet",
  },
  assets: { label: "Assets", icon: Package, badgeColor: "emerald" },
  security_interests: {
    label: "Security Interests",
    icon: Shield,
    badgeColor: "orange",
  },
  financial_ledgers: {
    label: "Financial Ledgers",
    icon: BarChart3,
    badgeColor: "indigo",
  },
  general_ledgers: {
    label: "General Ledgers",
    icon: BarChart3,
    badgeColor: "sky",
  },
  forecasts_budget: {
    label: "Forecasts & Budget",
    icon: BarChart3,
    badgeColor: "sky",
  },
  bank_statements_transactions: {
    label: "Bank Statements & Transactions",
    icon: Banknote,
    badgeColor: "teal",
  },
  general_liabilities: {
    label: "General Liabilities",
    icon: Receipt,
    badgeColor: "amber",
  },
  proof_of_debt: {
    label: "Proof of Debt",
    icon: ScrollText,
    badgeColor: "red",
  },
  employment: { label: "Employment", icon: Briefcase, badgeColor: "purple" },
  tax_and_sro: { label: "Tax & SRO", icon: Landmark, badgeColor: "pink" },
  loans: { label: "Loans", icon: Landmark, badgeColor: "sky" },
  legal_disputes: { label: "Legal Disputes", icon: Scale, badgeColor: "red" },
  insurance: { label: "Insurance", icon: Shield, badgeColor: "green" },
  other: { label: "Other", icon: FileText, badgeColor: "gray" },
};

function getCategoryDisplay(cat: string): {
  label: string;
  icon: LucideIcon;
  badgeColor: BadgeColor;
} {
  return (
    CATEGORY_CONFIG[cat] ?? {
      label: cat.replace(/_/g, " "),
      icon: FileText,
      badgeColor: "gray",
    }
  );
}

// ---------------------------------------------------------------------------
// Agent Icons — sourced from shared registry so sidebar + cards stay in sync
// (see ./agentRegistry.ts)
// ---------------------------------------------------------------------------

const STATUS_CIRCLE_STYLES: Record<string, { bg: string; icon: string }> = {
  running: {
    bg: "bg-emerald-100 dark:bg-emerald-900/40",
    icon: "text-emerald-500 dark:text-emerald-400 animate-spin",
  },
  completed: {
    bg: "bg-emerald-100 dark:bg-emerald-900/40",
    icon: "text-emerald-600 dark:text-emerald-500",
  },
  failed: {
    bg: "bg-red-100 dark:bg-red-900/40",
    icon: "text-destructive",
  },
  cancelled: {
    bg: "bg-muted/50",
    icon: "text-muted-foreground/40",
  },
  skipped: {
    bg: "bg-muted/50",
    icon: "text-muted-foreground/30",
  },
  waiting: {
    bg: "bg-muted/50",
    icon: "text-muted-foreground/40",
  },
};

/**
 * Renders the agent-specific icon in a status-coloured circle.
 * Falls back to a generic Loader2 spinner when running.
 */
export function AgentIcon({
  agentId,
  status,
}: {
  agentId: string;
  status: string;
}) {
  const styles = STATUS_CIRCLE_STYLES[status] ?? STATUS_CIRCLE_STYLES.waiting;

  const Icon =
    status === "running" ? Loader2 : (AGENT_ICONS[agentId] ?? FileText);

  return (
    <div
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
        styles.bg,
      )}
    >
      <Icon className={cn("h-4 w-4", styles.icon)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent Card
// ---------------------------------------------------------------------------

interface AgentCardProps {
  task: {
    _id?: Id<"summarizationTasks">;
    agentId: string;
    agentName: string;
    categories: string[];
    /**
     * Task status. For agents that have not yet been triggered by a document,
     * pass `"waiting"` so the card renders the proactive "Waiting for documents"
     * affordance.
     */
    status: string;
    duration?: number;
    documentCount: number;
    documentsAnalysed: number;
    overallAssessment?: string;
    error?: string;
    concernCount: number;
    latestActivity?: string;
    /** Firm workflow status (org taskStatuses) — MOR-736. */
    workflowStatusId?: Id<"taskStatuses">;
    /**
     * A re-run is queued (debounced timer or coalesced lock) even though
     * `status` may still read "completed" — badge the agent as "Queued".
     */
    pendingRerun?: boolean;
  };
  /** Optional UI blurb sourced from the manifest. */
  uiDescription?: string;
  isViewer?: boolean;
  /** Org for the firm workflow-status dropdown (MOR-736). */
  orgId?: Id<"organizations"> | null;
  /** Set / clear the firm workflow status on this agent (MOR-736). */
  onWorkflowStatusChange?: (statusId: Id<"taskStatuses"> | null) => void;
  /** Open the detail view for this agent. No-op if the task hasn't been created yet. */
  onSelect: () => void;
  /**
   * Force a fresh run for this agent. Hidden when the task has not been
   * created yet (since there is nothing to re-run).
   */
  onRerun?: () => void;
  /**
   * Cancel an in-flight run. Surfaced on hover while the task is running so
   * practitioners can stop a long-running agent mid-flight without losing
   * the Anthropic session (next document arrival picks back up).
   */
  onCancel?: () => void;
  /** When true, renders as a row inside a group card (no border/rounding) */
  grouped?: boolean;
}

export function AgentCard({
  task,
  uiDescription,
  isViewer,
  orgId,
  onWorkflowStatusChange,
  onSelect,
  onRerun,
  onCancel,
  grouped,
}: AgentCardProps) {
  const isWaiting = task.status === "waiting";
  const isRunning = task.status === "running";
  const isCompleted = task.status === "completed";
  const isFailed = task.status === "failed";
  const isCancelled = task.status === "cancelled";
  const hasTaskRow = task._id !== undefined;
  // All cards are openable — the agent page handles the no-task-yet state
  // by rendering a "waiting for documents" placeholder. This lets users
  // navigate to an agent's page from the sidebar before its first run.
  const isInteractive = true;

  return (
    <div
      className={cn(
        "group transition-colors duration-150",
        grouped
          ? cn(
              "bg-background hover:bg-muted/40",
              isRunning && "bg-emerald-50/30 dark:bg-emerald-950/10",
              isFailed && "bg-destructive/5",
            )
          : cn(
              "rounded-lg border bg-background",
              isRunning &&
                "border-emerald-200 bg-emerald-50/20 dark:border-emerald-800 dark:bg-emerald-950/10",
              isCompleted && "border-border hover:border-border/80",
              isFailed && "border-destructive/30",
              (isWaiting || isCancelled) && "border-border",
            ),
      )}
    >
      <div
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={isInteractive ? onSelect : undefined}
        onKeyDown={
          isInteractive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") onSelect();
              }
            : undefined
        }
        className={cn("w-full text-left", isInteractive && "cursor-pointer")}
      >
        <div
          className={cn(
            "flex items-center gap-4 py-3.5",
            grouped ? "px-5" : "px-5 py-4",
          )}
        >
          {/* Agent icon */}
          <div className="shrink-0">
            <AgentIcon agentId={task.agentId} status={task.status} />
          </div>

          {/* Main content */}
          <div className="min-w-0 flex-1">
            {/* Title row */}
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-semibold text-foreground">
                {task.agentName}
              </span>
              {(isRunning ||
                (isWaiting && hasTaskRow) ||
                task.pendingRerun) && (
                <AgentActivityBadge
                  status={task.status}
                  pendingRerun={task.pendingRerun}
                />
              )}
              {isCompleted && task.duration != null && (
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatDurationShort(task.duration)}
                </span>
              )}
            </div>

            {/* Categories + doc count */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {task.categories.map((cat) => {
                const cfg = getCategoryDisplay(cat);
                return (
                  <Badge
                    key={cat}
                    variant="default"
                    size="sm"
                    className="bg-primary/80 hover:bg-primary"
                  >
                    {cfg.label}
                  </Badge>
                );
              })}
              <Badge variant="outline" size="sm" className="gap-1 tabular-nums">
                <FileText className="h-3 w-3" />
                {task.documentCount}
              </Badge>
            </div>

            {/* Status line */}
            {isRunning && task.latestActivity && (
              <ActivityLine message={task.latestActivity} />
            )}
            {isFailed && task.error && (
              <p className="mt-2 line-clamp-1 text-xs text-destructive">
                {task.error}
              </p>
            )}
            {isWaiting && uiDescription && (
              <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                {uiDescription}
              </p>
            )}
          </div>

          {/* Action area */}
          <div
            className="flex shrink-0 items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {hasTaskRow && orgId && onWorkflowStatusChange && (
              <WorkflowStatusSelect
                orgId={orgId}
                value={task.workflowStatusId ?? null}
                onChange={onWorkflowStatusChange}
                disabled={isViewer}
              />
            )}
            {isWaiting && !hasTaskRow && (
              <span
                className="inline-flex h-9 items-center gap-1.5 px-3 text-xs font-medium text-muted-foreground"
                title="This agent runs automatically when documents are indexed for its categories."
              >
                <Clock className="h-3.5 w-3.5" />
                Waiting for documents
              </span>
            )}
            {isCancelled && (
              <span className="inline-flex h-9 items-center gap-1.5 px-3 text-xs font-medium text-muted-foreground">
                <XCircle className="h-3.5 w-3.5" />
                Cancelled
              </span>
            )}
            {(isFailed || isCancelled || (isWaiting && hasTaskRow)) &&
              onRerun && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRerun}
                  disabled={isViewer}
                  className="h-9 gap-1.5"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {isFailed ? "Retry" : "Re-run"}
                </Button>
              )}
            {isRunning && onCancel && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onCancel}
                disabled={isViewer}
                className="h-9 gap-1 text-destructive opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                title="Cancel this run (agent session is preserved)"
              >
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            )}
            {isRunning && onRerun && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onRerun}
                disabled={isViewer}
                className="h-9 gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                title="Force re-run (use if stuck)"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
            {isCompleted && (
              <>
                {onRerun && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onRerun}
                    disabled={isViewer}
                    className="h-9 gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button size="sm" onClick={onSelect} className="h-9 gap-1.5">
                  Open
                </Button>
              </>
            )}
            {isRunning && (
              <ChevronRight className="h-4 w-4 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity line — pill rendering that mirrors TimelineToolCallEntry so the
// agent card and the unified timeline read the same way.
// ---------------------------------------------------------------------------

function ActivityLine({ message }: { message: string }) {
  const parsed = parseActivityMessage(message);

  if (parsed.kind === "tool_call" || parsed.kind === "tool_result") {
    const isCall = parsed.kind === "tool_call";
    return (
      <div className="mt-2 flex min-w-0 items-center gap-2">
        <Badge
          color={getToolColor(parsed.toolName)}
          variant="soft"
          size="sm"
          className={cn(
            "shrink-0 gap-1 font-medium tabular-nums",
            isCall &&
              "animate-pulse ring-1 ring-blue-300/50 dark:ring-blue-700/40",
          )}
        >
          {getToolIcon(parsed.toolName)}
          {formatToolName(parsed.toolName)}
        </Badge>
        <span className="truncate text-[10px] text-muted-foreground/70">
          {isCall ? "Running\u2026" : "Returned"}
        </span>
      </div>
    );
  }

  if (parsed.kind === "callout") {
    return (
      <div className="mt-2 flex min-w-0 items-center gap-1.5">
        <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />
        <span className="line-clamp-1 text-xs text-amber-700 dark:text-amber-400">
          {parsed.body}
        </span>
      </div>
    );
  }

  if (parsed.kind === "error") {
    return (
      <p className="mt-2 line-clamp-1 text-xs text-destructive">
        {parsed.body}
      </p>
    );
  }

  return (
    <p className="mt-2 line-clamp-1 text-xs text-emerald-500 dark:text-emerald-400">
      {parsed.body}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDurationShort(ms: number): string {
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  return remainSecs > 0 ? `${mins}m ${remainSecs}s` : `${mins}m`;
}
